'use client';
/**
 * DIRE CE QUE LA SUPPRESSION EMPORTE — AVANT DE L'EMPORTER.
 * ───────────────────────────────────────────────────────────────────────────
 *
 * C'est la ligne tenue partout sur ce projet : le retrait d'une fichette de
 * recueil affiche ses usages rubrique par rubrique, le retrait d'un
 * destinataire exige un motif et refuse si l'accusé est signé. Trois routes de
 * retrait étaient pourtant autorisées SANS écran (GED-15 §3.1). Les brancher
 * avec un `confirm()` aurait donné exactement la suppression aveugle que ce
 * projet cherche à empêcher — d'où ce panneau, unique pour les trois.
 *
 * ── Ce qu'il garantit ──────────────────────────────────────────────────────
 *
 *  1. **Rien ne part sans que les conséquences soient à l'écran.** Elles sont
 *     passées par l'appelant, calculées sur des DONNÉES et non sur une
 *     intuition : « 3 dossiers portent un résultat sur cette ligne », pas
 *     « attention, cette action est irréversible ».
 *
 *  2. **Une conséquence `bloquant` retire le bouton.** Elle ne le grise pas :
 *     elle le remplace par un refus motivé. Un objet opposable — une décision
 *     d'habilitation prononcée, une série citée dans un document en vigueur —
 *     ne se retire pas « avec confirmation », il ne se retire pas.
 *
 *  3. **Annuler n'écrit RIEN.** Aucun appel réseau n'est fait avant le clic sur
 *     le bouton d'action : `onAnnuler` ne fait que fermer. C'est vérifiable à
 *     l'onglet réseau, et c'est le sens de la recette demandée.
 *
 *  4. **La nature du geste est dite.** `retrait` (réversible, l'objet est
 *     désactivé et reste lisible) et `suppression` (la donnée disparaît) ne se
 *     présentent pas de la même façon : les confondre ferait hésiter sur l'un
 *     et cliquer trop vite sur l'autre.
 */
import React, { useState } from 'react';
import { I, card, useBusy } from '@/components/ui';

import { Bouton } from '@/shared/Bouton';
export type Gravite = 'info' | 'attention' | 'bloquant';

/** Ce qu'emporte le geste, une ligne par effet établi. */
export type Consequence = {
  cle: string;
  /** L'effet, au présent et chiffré. « 3 dossiers de grille portent un résultat
   *  sur cette ligne », pas « des dossiers peuvent être concernés ». */
  texte: string;
  gravite: Gravite;
  /** Pourquoi cet effet compte — affiché sous le texte, en gris. Facultatif. */
  detail?: string;
};

const TONS: Record<Gravite, { fond: string; trait: string; icone: string }> = {
  info: { fond: 'var(--surface-sunken)', trait: 'var(--text-muted)', icone: 'check' },
  attention: { fond: '#FCF2E1', trait: 'var(--amber-600)', icone: 'alert' },
  bloquant: { fond: '#FDECEC', trait: 'var(--red-600)', icone: 'lock' },
};

export function SuppressionRaisonnee({
  titre, objet, nature, consequences, libelleAction, onConfirmer, onAnnuler, note,
}: {
  titre: string;
  /** L'objet visé, nommé — pas son identifiant. */
  objet: React.ReactNode;
  nature: 'retrait' | 'suppression';
  consequences: Consequence[];
  libelleAction: string;
  /** Doit rendre un message d'erreur, ou une chaîne vide en cas de succès. */
  onConfirmer: () => Promise<string>;
  onAnnuler: () => void;
  note?: React.ReactNode;
}) {
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();
  const bloquants = consequences.filter((c) => c.gravite === 'bloquant');
  const refuse = bloquants.length > 0;

  const agir = () => run('go', async () => {
    setErreur('');
    const msg = await onConfirmer();
    if (msg) setErreur(msg);
  });

  return (
    <div style={{
      ...card, padding: 16, display: 'grid', gap: 12,
      borderInlineStart: `3px solid ${refuse ? 'var(--red-600)' : 'var(--amber-600)'}`,
    }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>{titre}</div>
        <div style={{ fontSize: 12.5, color: 'var(--text-body)', marginTop: 3, lineHeight: 1.5 }}>{objet}</div>
      </div>

      <div>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 6 }}>
          Ce que ce {nature} emporte
        </div>
        <div style={{ display: 'grid', gap: 6 }}>
          {consequences.length === 0 && (
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
              Rien d’autre n’en dépend : aucun enregistrement ne s’y réfère aujourd’hui.
            </div>
          )}
          {consequences.map((c) => {
            const t = TONS[c.gravite];
            return (
              <div key={c.cle} style={{
                display: 'flex', gap: 8, alignItems: 'flex-start',
                background: t.fond, borderRadius: 'var(--radius-sm)', padding: '7px 11px',
              }}>
                {/* ⚠️ `alert`, `lock`, `check` sont dans la table de `I` : un nom
                    inconnu y rend un SVG VIDE, sans erreur. */}
                <span style={{ color: t.trait, flexShrink: 0, lineHeight: 0, marginTop: 2 }}><I n={t.icone} s={13} /></span>
                <span style={{ fontSize: 12.5, lineHeight: 1.5, color: 'var(--text-body)' }}>
                  <span style={{ color: c.gravite === 'info' ? 'var(--text-body)' : t.trait, fontWeight: c.gravite === 'info' ? 400 : 600 }}>{c.texte}</span>
                  {c.detail && <span style={{ display: 'block', color: 'var(--text-muted)', fontSize: 12.5, marginTop: 2 }}>{c.detail}</span>}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {note && <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>{note}</div>}
      {!!erreur && <div style={{ fontSize: 12.5, color: 'var(--red-600)', fontWeight: 600 }}>{erreur}</div>}

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        {refuse ? (
          // Pas de bouton grisé : un bouton grisé se cherche encore. Le geste
          // n'existe pas tant que l'obstacle n'est pas levé, et l'obstacle est
          // nommé juste au-dessus.
          <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--red-600)' }}>
            {nature === 'retrait' ? 'Retrait refusé' : 'Suppression refusée'} — {bloquants.length === 1 ? 'le motif est' : 'les motifs sont'} ci-dessus.
          </span>
        ) : (
          <Bouton variante={nature === 'suppression' ? 'danger' : 'contour'} onClick={() => void agir()} disabled={estOccupe()}>
            <I n="trash" s={13} />{estOccupe('go') ? 'En cours…' : libelleAction}
          </Bouton>
        )}
        {/* ⚠️ « REVENIR », ET SURTOUT PAS « ANNULER » (relevé SYS-7 du
            26/08/2026). Sur l'annulation d'une non-conformité, les deux boutons
            se lisaient « Annuler la non-conformité » et « Annuler », côte à
            côte — le même verbe pour deux gestes OPPOSÉS, dont l'un est
            irréversible. Le second ne défait rien : aucun appel réseau n'a été
            émis à ce stade, il ferme la fenêtre. « Revenir » le dit ; « Annuler »
            invite à croire qu'on annule l'annulation. */}
        <Bouton taille="sm" onClick={onAnnuler} disabled={estOccupe()}>
          {refuse ? 'Fermer' : 'Revenir'}
        </Bouton>
      </div>
    </div>
  );
}
